/**
 * Shared onboarding content constants.
 * Single source of truth for onboarding flow content across web and mobile.
 * Platform-specific rendering (icons, components) is handled by each platform.
 */

import { getTrackingLimit } from './featureAccess';
import { TIER_PICKS_QUOTAS, TIER_PRICING } from './subscriptionTier';

// =============================================================================
// Step 1: Value Props
// =============================================================================

export interface ValueProp {
  title: string;
  description: string;
  /** Icon name from lucide — each platform maps to its own icon component */
  iconName: 'Lightbulb' | 'TrendingUp' | 'ShieldCheck';
}

export const ONBOARDING_VALUE_PROPS: ValueProp[] = [
  {
    title: 'Understand',
    description: 'See AI confidence scores, scoring factors, and full reasoning for every pick',
    iconName: 'Lightbulb',
  },
  {
    title: 'Learn',
    description: 'The AI improves from community results — wins AND losses',
    iconName: 'TrendingUp',
  },
  {
    title: 'Trust',
    description: 'Public leaderboard shows our real record — no cherry-picking',
    iconName: 'ShieldCheck',
  },
];

// =============================================================================
// Step 2: Free Tier Features
// =============================================================================

export interface FreeTierFeature {
  label: string;
  /** Icon name from lucide */
  iconName: 'Zap' | 'Lightbulb' | 'Package' | 'ClipboardCheck' | 'Trophy' | 'BarChart3';
}

export function getOnboardingFreeFeatures(): FreeTierFeature[] {
  return [
    {
      label: `${TIER_PICKS_QUOTAS.free.standard} AI parlay sets/day`,
      iconName: 'Zap',
    },
    {
      label: 'Full pick reasoning & confidence scores',
      iconName: 'Lightbulb',
    },
    {
      label: 'Parlay builder',
      iconName: 'Package',
    },
    {
      label: `${getTrackingLimit('free')} tracked bets/day`,
      iconName: 'ClipboardCheck',
    },
    {
      label: 'Community leaderboard',
      iconName: 'Trophy',
    },
    {
      label: 'Basic analytics',
      iconName: 'BarChart3',
    },
  ];
}

// =============================================================================
// Step 4: Smart Betting Tips
// =============================================================================

export interface BettingTipCard {
  title: string;
  description: string;
  /** Icon name from lucide */
  iconName: 'Eye' | 'Hammer' | 'FileSearch';
}

export const ONBOARDING_BETTING_TIPS: BettingTipCard[] = [
  {
    title: 'Watch Before You Wager',
    description:
      'Run generations for a few days and track picks before betting real money. Learn which settings work for you.',
    iconName: 'Eye',
  },
  {
    title: 'Build Your Own',
    description:
      'Cherry-pick the best legs from different parlays and combine them with your own research in the Parlay Builder.',
    iconName: 'Hammer',
  },
  {
    title: 'Read the Analysis',
    description:
      'Check the full scoring breakdown for every leg — not just the score badge. Understand why each pick was chosen.',
    iconName: 'FileSearch',
  },
];

// =============================================================================
// Step 5: Tier Highlights
// =============================================================================

export interface TierHighlight {
  name: string;
  tier: 'pro' | 'premium' | 'elite';
  price: string;
  features: string[];
}

export function getOnboardingTierHighlights(): TierHighlight[] {
  return [
    {
      name: 'Pro',
      tier: 'pro',
      price: `${TIER_PRICING.pro.price}${TIER_PRICING.pro.period}`,
      features: [
        `${TIER_PICKS_QUOTAS.pro.standard} picks/day`,
        'Line shopping & best odds',
        'All themes',
        'See your rank & view profiles',
      ],
    },
    {
      name: 'Premium',
      tier: 'premium',
      price: `${TIER_PRICING.premium.price}${TIER_PRICING.premium.period}`,
      features: [
        `${TIER_PICKS_QUOTAS.premium.detailed} detailed AI picks/day`,
        'Injury & line movement alerts',
        'Custom themes',
        'Deep scoring analysis',
      ],
    },
    {
      name: 'Elite',
      tier: 'elite',
      price: `${TIER_PRICING.elite.price}${TIER_PRICING.elite.period}`,
      features: [
        'AI Chat Assistant',
        'Sharp money signals & +EV badges',
        'Prediction math breakdown',
        'Everything in Premium',
      ],
    },
  ];
}
